import { Box, Badge, MenuList, MenuItem, Popper, Paper, ClickAwayListener, Typography } from '@material-ui/core';
import { Drafts, Email, NotificationsNone } from '@material-ui/icons';
import { useHistory } from 'react-router';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { deepCloneElement } from '../../../../core/utils/utils';
import { mercure } from '../../../../core/utils';
import styles from './Alerts.module.scss';

type Props = {
    login: string;
};

const Alerts: React.FC<Props> = ({ login }) => {
    const { t } = useTranslation();
    const [alertList, setAlertList] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const history = useHistory<History>();
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popper' : undefined;

    const addAlert = (event) => {
        // Check if type already exist to prevent doubles
        let copy = deepCloneElement(alertList);
        copy = copy.filter((o) => o.type !== event.type);
        // If event has property count and if count > 0 then we add it to the alert list
        if (!event.data.hasOwnProperty('count') || (event.data.hasOwnProperty('count') && event.data.count > 0)) {
            copy.push(event);
        }
        setAlertList(copy);
    };

    const removeAlertType = (type) => {
        if (type === 'ALL') {
            setAlertList([]);
        } else {
            let copy = deepCloneElement(alertList);
            copy = copy.filter((o) => o.type !== type);
            setAlertList(copy);
        }
    };

    const createMercureListener = (id, mercureFunction, login) => {
        return mercure.createListener(id, mercureFunction, login);
    };

    useEffect(() => {
        let eventSource = null;
        if (login) {
            eventSource = createMercureListener('alert', addAlert, encodeURIComponent(login));
        }

        return () => {
            if (eventSource) eventSource.close();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const getDomAlert = (alert) => {
        switch (alert.type) {
            case 'SEARCH_BIA':
                return (
                    !!alert.data.count && (
                        <MenuItem
                            classes={{ root: styles['alert-item'] }}
                            key={alert.data}
                            onClick={() => {
                                handleClose();
                                removeAlertType('SEARCH_BIA');
                                history.push('/affiliations/bias');
                            }}
                        >
                            <Box display="flex" justifyContent="space-between" width="100%">
                                <Box>{t('header.alertes.newBias', { count: alert.data.count })}</Box>
                                <Box className={styles['alert-arrow']}>
                                    <span className="fas fa-arrow-right"></span>
                                </Box>
                            </Box>
                        </MenuItem>
                    )
                );
            default:
                return (
                    <MenuItem classes={{ root: styles['alert-item'] }}>
                        <Box>{t('header.alertes.label', { type: alert.type })}</Box>
                    </MenuItem>
                );
        }
    };

    return (
        <>
            <Box
                className={classnames(styles.bell, alertList.length > 0 && 'action')}
                onClick={handleClick}
                aria-describedby={id}
            >
                <Badge badgeContent={alertList.length} color="error">
                    <NotificationsNone />
                </Badge>
            </Box>
            <Popper
                open={open}
                placement={'bottom-end'}
                anchorEl={anchorEl}
                transition
                className={styles['alert-popup']}
            >
                <ClickAwayListener onClickAway={handleClose}>
                    <Paper elevation={3} classes={{ root: styles['alert-paper'] }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={1}>
                            <Typography variant="h4" className="m-0">
                                {t('header.alertes.notifications')}
                            </Typography>
                            <Box className={styles['alert-close']} onClick={handleClose}>
                                <span className="fas fa-times"></span>
                            </Box>
                        </Box>
                        <Box className={styles['alert-delimiter']}></Box>
                        {alertList.length > 0 ? (
                            <>
                                <Box display="flex" justifyContent="flex-end" p={1}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        className={styles['alert-seeAll']}
                                        onClick={() => {
                                            removeAlertType('ALL');
                                        }}
                                    >
                                        <Box className={styles['alert-seeAll-text']} mr={0.5}>
                                            {t('header.alertes.seeAll')}
                                        </Box>
                                        <Email fontSize="small" className={styles['alert-email-closed']}></Email>
                                        <Drafts fontSize="small" className={styles['alert-email-opened']}></Drafts>
                                    </Box>
                                </Box>
                                <Box p={2} pt={0}>
                                    <MenuList classes={{ root: styles['alert-container'] }} id="menu-list-grow">
                                        {alertList.map((alert) => getDomAlert(alert))}
                                    </MenuList>
                                </Box>
                            </>
                        ) : (
                            <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                className={styles['alert-none']}
                            >
                                {t('header.alertes.none')}
                            </Box>
                        )}
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>
    );
};

export default Alerts;
